export const boluswizard = `


<div style="display: flex; justify-content: center;">
  <img src="https://cdn-images-1.medium.com/max/2476/1*1ZGB69JKVRLNKDJxyuHIlQ.png" alt="The Bolus wizard at its best" style="max-width: 100%; width: 600px; height: auto;" />
</div>

If you're using, or thinking about using, AndroidAPS for managing your diabetes, you're about to step into a new level of control over your health.

In this post we'll walk through the essentials and share practical tips so you can confidently start to master the Bolus Wizard.

If you have any questions or need guidance, feel free to reach out to our team at [Opensource.clinic](https://www.opensource.clinic/).

<h2 style="font-size: 2rem; margin-top: 2em;">What it does</h2>

The Bolus wizard makes your life easier in the following ways:

* <b>Simplifies Math</b>: The Bolus wizard takes care of the nitty gritty of the calculations 😅.
* <b>Helps taking into account multiple factors</b>: It can calculate your corrected insulin need based on factors like current highs or lows, the amount of insulin you have in your body from previous administrations (insulin-on-board or "IOB"), or remaining carbs after a recent meal.
* <b>Improves stability</b>: Especially when used together with the "Eating Soon" mode (a special mode that makes your body ready for eating food), it's possible to achieve impressive flat glucose curves after carb-rich meals.
* <b>Saves your data</b>: If connected to a Nightscout server, the bolus and carbohydrate amounts are automatically uploaded. This data can then be monitored in real-time or reviewed later by you or anyone you choose to share it with.

<h2 style="font-size: 2rem; margin-top: 2em;">Basic features</h2>

The Bolus wizard has the following basic functionalities.

<div style="display: flex; justify-content: center;">
  <img src="https://cdn-images-1.medium.com/max/2000/1*-5hgrOCLNAl9DjKRciyrCw.png" alt="Basic features" style="max-width: 100%; width: 600px; height: auto;" />
</div>

<h1 style="font-size: 2.2rem; margin-top: 2.4em;">1. Carbs</h1>

<div style="font-size: 1.18rem;">
In the middle section of the Bolus wizard you can input the amount of carbs that you are about to eat. It's difficult to exactly estimate the amount of carbs in a meal down to the gram, but there are multiple apps that can assist you. Smartphone apps like <b><a href="https://www.snaq.ai/">Snaq</a></b> and <b><a href="https://gluroo.com/">Gluroo</a></b> use photos from your smartphone to make a fairly reliable estimate of the amount of carbs in your meal. There is also <b><a href="https://www.youtube.com/shorts/zv90QcXjzYQ">ChatCGM</a></b>, an app that directly sends your glucose values to Nightscout or AndroidAPS. It works by sending your data to the app via a Telegram conversation. Even if you use the latest smartphone apps to reduce the stress of counting carbs, always have a <b>(digital) kitchen scale</b> in your kitchen to do fast, reliable, and accurate carb assessments when you're preparing your meals.
</div>

<iframe src="https://medium.com/media/41be25d6139b5b058d2417248319729a" frameborder=0></iframe>

<h1 style="font-size: 2.2rem; margin-top: 2.4em;">2. Corr</h1>

<div style="font-size: 1.18rem;">
You can use the bottom section "Corr" to <em>correct</em> the amount of insulin that's being administered by your loop. This is often used to create a safety margin in the amount of insulin administered.

Adjustments should be made based on the type of expected carb release in the meal: fast-acting carbs, slow-acting carbs, or a mixture (medium-releasing meals). To get an idea of meals that lead to a fast, medium, or slow release, take a look at this <a href="https://chatgpt.com/share/67214878-e648-8005-bc1c-dfb7ff97120a">ChatGPT conversation</a> (and ask additional questions if needed).

You can use the following protocol to stay on the safe side.
</div>

<h3 style="font-size: 1.04rem; margin-top: 1.7em; font-weight: 700;">Fast releasing meals (white bread, fruit juices, pastries)</h3>

<ul style="font-size: 1.04rem;">
<li>If you're in a safe range, let's say under 8 mmol/L, you can use around 70% of the total amount of carbs and let AndroidAPS correct for the rest if needed. AndroidAPS will do this with <a href="https://androidaps.readthedocs.io/en/latest/DailyLifeWithAaps/KeyAapsFeatures.html#super-micro-bolus-smb">super micro boluses</a>, referred to as SMBs. These are small boluses given automatically to correct for high blood sugars.</li>
<li>If you're above 8 mmol/L when you start eating, you might opt for a higher percentage of insulin, around 80%, that covers the total amount of carbs.</li>
</ul>

<h3 style="font-size: 1.04rem; margin-top: 1.7em; font-weight: 700;">Medium releasing meals (whole grain bread, quinoa, sweet potatoes)</h3>

<ul style="font-size: 1.04rem;">
<li>You can use 50–70% of the total amount of carbs and let AndroidAPS correct for the rest with SMBs.</li>
<li>Use the remaining 30–50% of the carbs as input for eCarbs. Start the eCarbs 60–90 minutes into the future and spread out the eCarbs over a time duration of 2 (rice, quinoa) to 6 (pizza, pasta) hours.</li>
</ul>

<div style="display: flex; justify-content: center;">
  <img src="https://cdn-images-1.medium.com/max/2156/1*amrikLs1jb-4kD9W7SAQIA.png" alt="ecarbs button" style="max-width: 100%; width: 600px; height: auto;" />
</div>

<div style="display: flex; justify-content: center;">
  <img src="https://cdn-images-1.medium.com/max/2000/1*bQdn96PNhR6FFpd5w5ji2Q.png" alt="The ecarbs menu" style="max-width: 100%; width: 600px; height: auto;" />
</div>

<h3 style="font-size: 1.04rem; margin-top: 1.7em; font-weight: 700;">Slow releasing meals (brown rice, pizza, pasta)</h3>

<ul style="font-size: 1.04rem;">
<li>Use around 50% of the total amount of carbs as input for carbs in the Bolus wizard.</li>
<li>Use the other 50% as input for the eCarbs. Spread out the eCarbs over a time period of 2 (rice, quinoa) to 6 (pizza, pasta) hours.</li>
</ul>

<h3 style="font-size: 1.04rem; margin-top: 1.7em; font-weight: 700;">Physical inactivity</h3>
<div style="font-size: 1.04rem;">
If you are going to watch a movie 🍿 or sit behind a desk 🧑‍💻 for a long period of time, you might want to add 10%–20% extra insulin above the mentioned percentages and subtract that from the eCarbs. The insulin will act slower due to inactivity and there will be a lower risk of hypos.
</div>

<h3 style="font-size: 1.04rem; margin-top: 1.7em; font-weight: 700;">Workouts</h3>
<div style="font-size: 1.04rem;">
If you're about to do an intensive workout within the next 4–6 hours, take preparatory measures described <a href="https://androidaps.readthedocs.io/en/latest/DailyLifeWithAaps/TempTargets.html#activity-temp-target">over here</a> and <a href="https://androidaps.readthedocs.io/en/latest/DailyLifeWithAaps/TempTargets.html#how-do-i-turn-off-smb-during-temp-targets">here</a>, and <b>try to use eCarbs instead of the Bolus wizard as much as possible to prevent hypos</b>. You can spread them out over a period of 1–3 hours for fast to medium release meals with carbs or 4–6 hours for slow meals with slow release carbs. Only in edge cases, for instance large meals with total carb content above 60–90 grams, use 10–50% of your eaten carbs as input for the Bolus wizard.
</div>

<h1 style="font-size: 2.2rem; margin-top: 2.4em;">3. BG</h1>

<div style="font-size: 1.18rem;">
In the BG section you can input the current glucose levels. This is rarely used and mostly for cases where a fingerstick test is needed (CGM glucose value not available, doubt about CGM measurement reliability, or no access to CGM).
</div>

<h2 style="font-size: 2rem; margin-top: 2.3em;">Additional options</h2>

<div style="font-size: 1.18rem;">
There are additional options which can be made visible by clicking on the checkbox near the eye icon.
</div>

<div style="display: flex; justify-content: center;">
  <img src="https://cdn-images-1.medium.com/max/2772/1*JoLwb8v9q5XoqheA9h_v0g.png" alt="Opening the extra options" style="max-width: 100%; width: 600px; height: auto;" />
</div>

<div style="font-size: 1.18rem;">
Some extra checkboxes will pop up, and they can be a bit tricky to figure out 😇
</div>

<div style="display: flex; justify-content: center;">
  <img src="https://cdn-images-1.medium.com/max/2000/1*oopavLZNvL7LrU6i3tI_Ig.png" alt="Extra checkboxes in the Bolus wizard" style="max-width: 100%; width: 600px; height: auto;" />
</div>

<h3 style="font-size: 1.04rem; margin-top: 1.7em; font-weight: 700;">When to use BG checkbox?</h3>

<div style="font-size: 1.04rem;">
Tick the <b>BG</b> box only if you're confident your glucose levels will stay steady for the next 15–45 minutes. If you're expecting a drop due to exercise, correcting a high glucose level might cause a hypo.

Same goes for when your glucose is low. For example, if you've already eaten something to fix a low and you're about to eat again, leave the BG checkbox unticked. Otherwise, you might end up with less insulin than you actually need.

You can also use this box if you see your pump or patch isn't working properly and you need to correct a high due to insulin that was not given, but AndroidAPS thinks it has been given 🫤. In these cases, tick the BG box, but don't add anything else.
</div>

<h3 style="font-size: 1.04rem; margin-top: 1.7em; font-weight: 700;">When to use 15min trend checkbox?</h3>
<div style="font-size: 1.04rem;">
Tick the 15-minute trend box if you notice your glucose is rising and you're already high. If you think the trend will keep going up for another 30–60 minutes. The same rule applies if your glucose is going down, and you expect that trend to continue. Just make sure the trend won't suddenly change (like if you're about to exercise 🚴🏋️‍♀️).
</div>

<h3 style="font-size: 1.04rem; margin-top: 1.7em; font-weight: 700;">When to use COB checkbox?</h3>
<div style="font-size: 1.04rem;">
Tick the COB (Carbs-on-Board) box if you've eaten carbs recently, but they aren't fully covered by your current insulin. For example:
<ul>
  <li>If you gave less insulin because you planned to exercise but then canceled the workout and need to correct for those carbs.</li>
  <li>Or, if you took insulin but had a technical issue (like your pump or phone dying 👺) and the bolus got interrupted and only a partial bolus has been given by AndroidAPS.</li>
</ul>
Ticking the COB box will automatically check the IOB (Insulin-on-Board) box as a safety measure.
</div>

<h3 style="font-size: 1.04rem; margin-top: 1.7em; font-weight: 700;">When to use IOB checkbox?</h3>
<div style="font-size: 1.04rem;">
Did you eat a meal with fast carbs 2–4 hours ago? Always tick the IOB box and leave COB unticked. The insulin you've already given will still be active, so you can subtract that from the amount you need to give now.
</div>

<h2 style="font-size: 2rem; margin-top: 2.3em;">Profile Changes</h2>

<div style="font-size: 1.18rem;">
If you've adjusted your profile for reasons like stress, illness, or weight changes, the Bolus wizard will use adjusted numbers based on the new profile for the calculation of a bolus. This can lead to under or overdosing if not used carefully.

For example, if you're used to bolusing 20 units of insulin for a particular meal, the Bolus wizard will indicate 25 units if the profile is 125%. However, you already have an increased basal insulin level so the extra insulin you need for the meal might very well already be covered by that. We therefore advise to use your 100% profile by selecting it in the Profile section shown below because the risk of overdosing is too high 👍🦺.
</div>

<div style="display: flex; justify-content: center;">
  <img src="https://cdn-images-1.medium.com/max/2000/1*T9iwnsM_rPiJNrUaxpmvwA.png" alt="Profile selection" style="max-width: 100%; width: 600px; height: auto;" />
</div>

<h2 style="font-size: 2rem; margin-top: 2.3em;">Always stay on the safe side</h2>

<div style="font-size: 1.18rem;">
While the Bolus wizard offers valuable assistance in managing insulin doses, it's essential to approach it with caution. Here are some key elements to be mindful of:
<ul>
<li><b>Time Lag in Glucose Sensing</b>: There can be between 5–15 min between the glucose values in your blood and the glucose value that you see on your mobile phone ⏰. This is important to consider when determining your insulin dose with the Bolus wizard.</li>
<li><b>Potential Calculation Inaccuracies</b>: Although the Wizard strives to provide perfect dosing, factors like the age of your sensor, the age of your cannula/infusion, and battery level of your pump can impact its precision. As a result, calculations of the needed amount of insulin may not always, and is actually never, entirely accurate.</li>
<li><b>Improve accuracy</b>: Easy methods to improve the accuracy are the routine aspects of self-care: refreshing cannula on time, measuring amount of carbs, eating healthy (foods that don't lead to large glucose variations), and staying physically active 🏃👟🎾.</li>
<li><b>Perfectionism</b>: Be careful with aiming for perfectly straight blood sugar lines. It can feel great to see a flat line at 6 mmol/L, but it leaves little room for error. If you're about to exercise or didn't eat enough carbs for an intense workout, your blood sugar could crash 😱. Always focus on preventing hypos before worrying about hypers. 👍</li>
</ul>
</div>

<div style="font-size: 1.18rem;">
For more info:
<ul>
  <li><a href="https://books.google.nl/books/about/Think_Like_a_Pancreas.html?id=PAZiTSvJuGgC&redir_esc=y"><em>How to Think Like a Pancreas</em></a> — it's a great resource for anyone managing diabetes.</li>
  <li>The <a href="https://androidaps.readthedocs.io/en/latest/DailyLifeWithAaps/AapsScreens.html#bolus-wizard">AndroidAPS docs</a> on the Bolus wizard.</li>
  <li>The <a href="https://www.facebook.com/groups/AndroidAPSUsers/">Facebook group of AndroidAPS</a> or <a href="https://www.opensource.clinic/diabetesmanagementapps">other open-source diabetes management Facebook groups</a>.</li>
  <li><a href="https://www.opensource.clinic/blog">Opensource.clinic</a></li>
</ul>
</div>

<!-- blog-contact-footer -->
`;
